import { Trash, X } from "@phosphor-icons/react";

/**
 * Sticky toolbar shown above a list when one or more rows are selected.
 * Renders the count, the bulk delete trigger, and an "X" to clear.
 *
 * The parent owns the actual deletion logic so each page can include
 * audit/sucursal/permission specifics.  This component is purely visual.
 */
export default function BulkActionBar({
  count,
  noun = "registro",
  pluralNoun,
  onDelete,
  onClear,
  busy = false,
  testid = "bulk-action-bar",
  extraActions = null,
}) {
  if (!count) return null;
  const label = count === 1 ? noun : pluralNoun || `${noun}s`;
  const liveMessage = `${count} ${label} seleccionado${count === 1 ? "" : "s"}`;
  return (
    <div
      className="sticky top-0 z-20 -mx-1 px-3 py-2 mb-3 flex items-center gap-3 bg-[#0F1A3B] text-white shadow-[0_2px_0_rgba(0,0,0,0.08)]"
      data-testid={testid}
    >
      <span
        className="text-sm font-medium tabular-nums"
        data-testid={`${testid}-count`}
        role="status"
        aria-live="polite"
      >
        {liveMessage}
      </span>
      <div className="flex-1" />
      {extraActions}
      <button
        type="button"
        onClick={onDelete}
        disabled={busy}
        className="inline-flex items-center gap-1.5 bg-[#FF2A00] hover:bg-[#E62500] disabled:opacity-60 disabled:cursor-wait text-white text-sm font-medium px-3 py-1.5"
        data-testid={`${testid}-delete`}
      >
        <Trash size={14} weight="bold" />
        {busy ? "Eliminando…" : "Eliminar seleccionados"}
      </button>
      <button
        type="button"
        onClick={onClear}
        disabled={busy}
        className="p-1.5 hover:bg-white/10 disabled:opacity-50"
        title="Limpiar selección"
        data-testid={`${testid}-clear`}
      >
        <X size={14} weight="bold" />
      </button>
    </div>
  );
}

/**
 * Compact button used inside `extraActions` for export/print/etc.
 */
export function BulkBarButton({ onClick, icon: Icon, label, testid, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 disabled:opacity-50 text-white text-sm font-medium px-3 py-1.5"
      data-testid={testid}
    >
      {Icon && <Icon size={14} weight="bold" />}
      {label}
    </button>
  );
}

/**
 * Compact select used inside `extraActions` for bulk-status changes.
 *
 *   <BulkBarSelect
 *     placeholder="Cambiar estado…"
 *     value=""                            // always reset to empty
 *     options={[{value:'pagado', label:'Pagado'}, ...]}
 *     onChange={(v) => handleBulkStatus(v)}
 *     testid="sales-bulk-bar-status"
 *   />
 */
export function BulkBarSelect({ value, placeholder, options, onChange, testid, disabled }) {
  return (
    <select
      value={value || ""}
      disabled={disabled}
      onChange={(e) => onChange?.(e.target.value)}
      className="bg-white/10 hover:bg-white/20 text-white text-sm font-medium px-3 py-1.5 border border-white/20 disabled:opacity-50 outline-none focus:bg-white/20 cursor-pointer"
      data-testid={testid}
    >
      <option value="" disabled className="text-zinc-900">
        {placeholder}
      </option>
      {options.map((o) => (
        <option key={o.value} value={o.value} className="text-zinc-900">
          {o.label}
        </option>
      ))}
    </select>
  );
}

/**
 * A tristate "select all" checkbox used in table headers.
 */
export function SelectAllCheckbox({ allSelected, someSelected, onChange, testid = "select-all" }) {
  return (
    <input
      type="checkbox"
      aria-label="Seleccionar todos"
      checked={allSelected}
      ref={(el) => {
        if (el) el.indeterminate = !!someSelected;
      }}
      onChange={onChange}
      className="w-4 h-4 accent-klein cursor-pointer"
      data-testid={testid}
    />
  );
}

/**
 * A row checkbox.  Stops propagation so it never triggers row-level handlers
 * (open detail, navigate, etc.).
 */
export function RowCheckbox({ checked, onChange, testid }) {
  return (
    <input
      type="checkbox"
      aria-label="Seleccionar fila"
      checked={!!checked}
      onChange={(e) => {
        e.stopPropagation();
        onChange?.(e);
      }}
      onClick={(e) => e.stopPropagation()}
      className="w-4 h-4 accent-klein cursor-pointer"
      data-testid={testid}
    />
  );
}

/**
 * Run a list of operations (delete, update, etc.) in parallel with a
 * concurrency cap so a 200-row selection doesn't melt the server.  Returns
 * { ok, fail, errors }.
 */
export async function runBulkAction(ids, action, { concurrency = 6 } = {}) {
  const queue = [...ids];
  let ok = 0;
  let fail = 0;
  const errors = [];

  async function worker() {
    while (queue.length) {
      const id = queue.shift();
      try {
        await action(id);
        ok += 1;
      } catch (e) {
        fail += 1;
        errors.push(e);
      }
    }
  }

  const workers = Array.from({ length: Math.min(concurrency, ids.length) }, () => worker());
  await Promise.all(workers);
  return { ok, fail, errors };
}

// Backwards-compatible alias.  Older callers still pass `deleteOne` here.
export const runBulkDelete = runBulkAction;
